import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, ArrowUp, Loader2, Sparkles } from 'lucide-react';
import { streamSse } from '../lib/api';
import { LotusMark } from './site/Logo';

interface Msg { role: 'user' | 'assistant'; content: string; tools?: string[] }

/**
 * The website rental assistant. Anonymous visitors get a deliberately narrow,
 * read-only toolset — search listings, look one up, book a viewing — enforced on the
 * server. It is the same agent loop the staff assistant uses; only the permission
 * scope differs.
 */
export function SiteChat({ initiallyOpen = false }: { initiallyOpen?: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('lotus:open-chat', onOpen);
    return () => window.removeEventListener('lotus:open-chat', onOpen);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  async function send(preset?: string) {
    const text = (preset ?? input).trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '', tools: [] }]);
    setStreaming(true);

    const patchLast = (fn: (m: Msg) => Msg) =>
      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last) next[next.length - 1] = fn(last);
        return next;
      });

    try {
      await streamSse('/ai/public-chat', { message: text }, {
        anonymous: true,
        onEvent: (event, data) => {
          const d = data as Record<string, unknown>;
          if (event === 'token') patchLast((m) => ({ ...m, content: m.content + String(d.text ?? '') }));
          if (event === 'tool_call') patchLast((m) => ({ ...m, tools: [...(m.tools ?? []), String(d.name ?? '')] }));
          if (event === 'error') patchLast((m) => ({ ...m, content: String(d.message ?? 'Sorry, something went wrong.') }));
        },
      });
    } catch {
      patchLast((m) => ({ ...m, content: m.content || 'Sorry — I could not reach the assistant just now.' }));
    } finally {
      setStreaming(false);
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="site-chat-fab group fixed bottom-6 right-6 z-40 flex items-center gap-3 rounded-full border border-ink-900 bg-ink-900 py-3 pl-4 pr-5 text-[13px] font-medium text-white shadow-float transition-all duration-500 hover:bg-white hover:text-ink-900"
      >
        <MessageCircle className="h-4 w-4" />
        Find me a home
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-40 flex h-[600px] max-h-[calc(100vh-3rem)] w-[min(26rem,calc(100vw-3rem))] flex-col overflow-hidden rounded-[22px] border border-ink-200 bg-white shadow-pop">
      <div className="flex items-center justify-between bg-ink-900 px-5 py-4 text-white">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-white/10"><LotusMark className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-semibold">Lotus Living</div>
            <div className="text-[11px] text-white/60">Ask about any of our homes</div>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-ink-50/60 p-5">
        {messages.length === 0 && (
          <div className="pt-2">
            <p className="display text-2xl text-ink-900">Hello — let's find <em>your</em> home.</p>
            <p className="mt-2 text-sm text-ink-500">Tell me where you'd like to live, your budget and when you want to move. I'm looking at live availability.</p>
            <div className="mt-5 space-y-2">
              {[
                'Two bedrooms in Hemel Hempstead under £1,300',
                'What is available in Streatham right now?',
                'Somewhere near Canary Wharf from next month',
              ].map((s) => (
                <button key={s} onClick={() => void send(s)} className="flex w-full items-center gap-2 rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-left text-[13px] text-ink-700 transition hover:border-bronze-400 hover:bg-bronze-50">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-bronze-500" />{s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex'}>
            <div className={m.role === 'user' ? 'max-w-[85%] rounded-2xl rounded-br-md bg-ink-900 px-4 py-2.5 text-sm text-white' : 'max-w-[92%] rounded-2xl rounded-bl-md border border-ink-200 bg-white px-4 py-3 text-sm text-ink-800'}>
              {!!m.tools?.length && (
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-bronze-500">
                  <Loader2 className={`h-3 w-3 ${streaming && i === messages.length - 1 ? 'animate-spin' : 'hidden'}`} />
                  checking availability
                </div>
              )}
              <span className="whitespace-pre-wrap leading-relaxed">{m.content}</span>
              {m.role === 'assistant' && !m.content && streaming && <span className="inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-bronze-400" />}
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-ink-200 bg-white p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-ink-200 bg-ink-50/60 p-1.5 focus-within:border-bronze-400 focus-within:ring-4 focus-within:ring-bronze-100">
          <textarea
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder="Type your message…"
            className="max-h-24 flex-1 resize-none bg-transparent px-2.5 py-2 text-sm text-ink-900 outline-none placeholder:text-ink-400"
          />
          <button onClick={() => void send()} disabled={streaming || !input.trim()} className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink-900 text-white transition hover:bg-bronze-500 disabled:opacity-30">
            <ArrowUp className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/** Any button on the site can open the assistant without prop drilling. */
export const openSiteChat = () => window.dispatchEvent(new Event('lotus:open-chat'));
